import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const CAT_LBL = {
    limpieza: "Limpieza",
    mantenimiento: "Mantenimiento",
    app_foresta: "App Foresta",
    internet: "Internet / WiFi",
    climatizacion: "Climatización",
    electricidad: "Electricidad",
    otros: "Otros",
    seguridad: "Seguridad",
};

const ESTADOS = {
    recibido: {
        label: "Recibido",
        chip: "bg-accent/10 text-accent border border-accent/30",
        mensaje: "Recibimos tu reclamo. Lo vamos a revisar.",
    },
    en_proceso: {
        label: "En proceso",
        chip: "bg-warning/15 text-warning border border-warning/40",
        mensaje: "Estamos trabajando en la solución.",
    },
    resuelto: {
        label: "Resuelto",
        chip: "bg-success/15 text-success border border-success/40",
        mensaje: "El reclamo fue resuelto.",
    },
};

function pad(n) { return String(n).padStart(2, "0"); }

function fmtFechaHora(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const ayer = new Date(today); ayer.setDate(today.getDate() - 1);
    const dDay = new Date(d); dDay.setHours(0, 0, 0, 0);
    const hh = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    if (dDay.getTime() === today.getTime()) return `Hoy · ${hh}`;
    if (dDay.getTime() === ayer.getTime()) return `Ayer · ${hh}`;
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} · ${hh}`;
}

export default function MisIncidencias() {
    const [items, setItems] = useState([]);
    const [delId, setDelId] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/incidencias/mias").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const remove = async () => {
        if (!delId) return;
        setBusy(true);
        try {
            await api.delete(`/incidencias/${delId}`);
            setItems((arr) => arr.filter((x) => x.incidencia_id !== delId));
            setDelId(null);
        } catch (e) {
            toast.error(formatApiError(e));
            await load();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-2xl">
            <PageHeader title="Mis Incidencias" subtitle="Seguimiento de tus reclamos." />

            {items.length === 0 ? (
                <EmptyState title="No tenés reclamos cargados." description="Cuando reportes una incidencia aparecerá acá." />
            ) : (
                <div className="grid gap-2.5">
                    {items.map((i) => {
                        const est = ESTADOS[i.estado] || ESTADOS.recibido;
                        return (
                            <Card key={i.incidencia_id} data-testid={`mi-inc-${i.incidencia_id}`}>
                                <CardContent className="p-4">
                                    <p className="font-heading font-semibold leading-tight">{i.titulo}</p>
                                    {i.descripcion && (
                                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{i.descripcion}</p>
                                    )}
                                    <p className="text-xs text-muted-foreground mt-1.5">
                                        {CAT_LBL[i.categoria] || i.categoria} · {fmtFechaHora(i.creado_en)}
                                    </p>

                                    <div className="mt-3 flex items-center gap-2">
                                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${est.chip}`}>
                                            {est.label}
                                        </span>
                                        <span className="text-xs text-muted-foreground">{est.mensaje}</span>
                                    </div>

                                    {i.comentarios?.length > 0 && (
                                        <div className="mt-3 border-t border-border pt-2 space-y-1">
                                            {i.comentarios.map((c) => (
                                                <p key={c.comentario_id} className="text-xs">
                                                    <span className="font-semibold">{c.autor_nombre}:</span> {c.comentario}
                                                </p>
                                            ))}
                                        </div>
                                    )}

                                    <div className="mt-3 flex justify-end">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 px-2"
                                            onClick={() => setDelId(i.incidencia_id)}
                                            data-testid={`del-${i.incidencia_id}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Eliminar
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Querés eliminar este reclamo?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Esta acción no se puede deshacer.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={remove}
                            disabled={busy}
                            data-testid="del-confirm"
                            className="bg-destructive hover:bg-destructive/90"
                        >
                            {busy ? "Eliminando…" : "Eliminar"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
